/* LUZ NOVA — PDP gallery swipe + pinch-zoom.
   Kept separate from global.js on purpose, same reasoning as cart-drawer.js:
   independently reviewable/revertible, and the existing thumbnail-click path
   in global.js (which sets #GalleryMain's src/srcset and toggles .is-active)
   stays the single source of truth for "which image is showing" — this file
   drives that same click handler with touch gestures instead of adding a
   second, competing way to change the image. Video support is intentionally
   out of scope for this pass. */
(function () {
  "use strict";

  var container = document.querySelector(".gallery__main");
  var img = document.getElementById("GalleryMain");
  if (!container || !img) return;

  var thumbs = Array.prototype.slice.call(
    document.querySelectorAll(".gallery__thumbs button[data-thumb]")
  );

  var currentIndex = 0;
  thumbs.forEach(function (t, i) {
    if (t.classList.contains("is-active")) currentIndex = i;
  });

  var scale = 1;
  var translateX = 0;
  var translateY = 0;

  function applyTransform() {
    img.style.transform =
      "translate(" + translateX + "px," + translateY + "px) scale(" + scale + ")";
    container.classList.toggle("is-zoomed", scale > 1);
  }

  function withTransition(fn) {
    img.classList.add("is-animating");
    fn();
    setTimeout(function () { img.classList.remove("is-animating"); }, 260);
  }

  function clampPan(x, y, s) {
    var rect = container.getBoundingClientRect();
    var maxX = Math.max(0, ((s - 1) * rect.width) / 2);
    var maxY = Math.max(0, ((s - 1) * rect.height) / 2);
    return [Math.min(maxX, Math.max(-maxX, x)), Math.min(maxY, Math.max(-maxY, y))];
  }

  function resetZoom(animated) {
    scale = 1;
    translateX = 0;
    translateY = 0;
    if (animated) { withTransition(applyTransform); } else { applyTransform(); }
  }

  /* Where to center the zoom, as a 0..1 fraction of the container in each
     axis — defaults to dead center (0.5, 0.5) for callers with no better
     information (a keyboard trigger, say). toggleZoom's actual callers below
     always pass the real cursor/tap position instead, which is the fix for
     "zoom always goes to the center and ignores the edges": scale used to
     apply around a fixed (0,0) pan target every time, so double-clicking
     near a corner still zoomed into the middle of the image rather than the
     spot the customer was actually looking at. */
  function toggleZoom(originX, originY) {
    if (scale > 1) {
      resetZoom(true);
    } else {
      scale = 2.5;
      var clamped = panForOrigin(
        typeof originX === "number" ? originX : 0.5,
        typeof originY === "number" ? originY : 0.5,
        scale
      );
      translateX = clamped[0];
      translateY = clamped[1];
      withTransition(applyTransform);
    }
  }

  /* Converts a 0..1 position within the container into the translate() that
     puts that exact point under the viewport's center — i.e. "zoom/pan
     toward here." At origin 0 (the image's left/top edge) this resolves to
     +max, and at origin 1 (right/bottom edge) to -max, so panning reaches
     every corner of the image at full clamped range instead of stopping
     short — the other half of "ignoring the edges." */
  function panForOrigin(originX, originY, s) {
    var rect = container.getBoundingClientRect();
    var maxX = ((s - 1) * rect.width) / 2;
    var maxY = ((s - 1) * rect.height) / 2;
    var x = maxX - originX * maxX * 2;
    var y = maxY - originY * maxY * 2;
    return clampPan(x, y, s);
  }

  /* Navigates by re-using the existing thumbnail click handler in global.js
     (sets src/srcset, toggles .is-active) rather than duplicating that logic
     here — one place decides how the image actually changes. */
  function goToIndex(i) {
    if (i < 0 || i >= thumbs.length || i === currentIndex) return false;
    currentIndex = i;
    thumbs[i].click();
    thumbs[i].scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    return true;
  }

  /* ---------- Touch gesture state ---------- */
  var gesture = null; /* "pending" | "swipe" | "pan" | "pinch" */
  var startX = 0, startY = 0;
  var panStartX = 0, panStartY = 0;
  var pinchStartDist = 0, pinchStartScale = 1;
  var lastTapTime = 0, lastTapX = 0, lastTapY = 0;

  function touchDist(touches) {
    var dx = touches[0].clientX - touches[1].clientX;
    var dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /* clientX/clientY (viewport pixels) -> a 0..1 fraction across the
     container in each axis, clamped to the container's own bounds. Feeds
     panForOrigin() so a zoom trigger lands on the exact spot the customer
     clicked or tapped instead of always the center. */
  function clientToOrigin(clientX, clientY) {
    var rect = container.getBoundingClientRect();
    var x = rect.width > 0 ? (clientX - rect.left) / rect.width : 0.5;
    var y = rect.height > 0 ? (clientY - rect.top) / rect.height : 0.5;
    return [Math.min(1, Math.max(0, x)), Math.min(1, Math.max(0, y))];
  }

  container.addEventListener("touchstart", function (evt) {
    if (evt.touches.length === 2) {
      gesture = "pinch";
      pinchStartDist = touchDist(evt.touches);
      pinchStartScale = scale;
      return;
    }
    if (evt.touches.length !== 1) return;

    var t = evt.touches[0];

    /* Double-tap to zoom */
    var now = Date.now();
    var dtX = Math.abs(t.clientX - lastTapX);
    var dtY = Math.abs(t.clientY - lastTapY);
    if (now - lastTapTime < 300 && dtX < 30 && dtY < 30) {
      var tapOrigin = clientToOrigin(t.clientX, t.clientY);
      toggleZoom(tapOrigin[0], tapOrigin[1]);
      gesture = null;
      lastTapTime = 0;
      return;
    }
    lastTapTime = now;
    lastTapX = t.clientX;
    lastTapY = t.clientY;

    startX = t.clientX;
    startY = t.clientY;

    if (scale > 1) {
      gesture = "pan";
      panStartX = translateX;
      panStartY = translateY;
    } else {
      gesture = "pending";
    }
  }, { passive: true });

  container.addEventListener("touchmove", function (evt) {
    if (gesture === "pinch" && evt.touches.length === 2) {
      evt.preventDefault();
      var dist = touchDist(evt.touches);
      var next = (pinchStartScale * dist) / pinchStartDist;
      scale = Math.min(4, Math.max(1, next));
      var clamped = clampPan(translateX, translateY, scale);
      translateX = clamped[0];
      translateY = clamped[1];
      applyTransform();
      return;
    }

    if (gesture === "pan" && evt.touches.length === 1) {
      evt.preventDefault();
      var t = evt.touches[0];
      var clamped2 = clampPan(panStartX + (t.clientX - startX), panStartY + (t.clientY - startY), scale);
      translateX = clamped2[0];
      translateY = clamped2[1];
      applyTransform();
      return;
    }

    if ((gesture === "pending" || gesture === "swipe") && evt.touches.length === 1) {
      var t2 = evt.touches[0];
      var dx = t2.clientX - startX;
      var dy = t2.clientY - startY;

      if (gesture === "pending") {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        gesture = Math.abs(dx) > Math.abs(dy) ? "swipe" : "scroll";
      }
      if (gesture !== "swipe") return; /* let the page scroll natively */

      evt.preventDefault();
      /* Rubber-band resistance at the first/last image */
      if ((currentIndex === 0 && dx > 0) || (currentIndex === thumbs.length - 1 && dx < 0)) {
        dx *= 0.35;
      }
      translateX = dx;
      translateY = 0;
      applyTransform();
    }
  }, { passive: false });

  container.addEventListener("touchend", function () {
    if (gesture === "pinch") {
      if (scale <= 1.02) resetZoom(true);
      gesture = null;
      return;
    }
    if (gesture === "pan") {
      gesture = null;
      return;
    }
    if (gesture === "swipe") {
      var dx = translateX;
      var moved = Math.abs(dx) > 50 && goToIndex(currentIndex + (dx < 0 ? 1 : -1));
      if (!moved) {
        withTransition(function () { translateX = 0; applyTransform(); });
      } else {
        translateX = 0;
        applyTransform();
      }
    }
    gesture = null;
  });

  container.addEventListener("touchcancel", function () {
    gesture = null;
    if (scale <= 1) { translateX = 0; translateY = 0; applyTransform(); }
  });

  /* ---------- Desktop mouse zoom + hover-pan ----------
     Double-click zooms in AT the clicked point (panForOrigin, above) rather
     than always centering. Previously that was the whole desktop
     interaction: zoomed in, and the only way to see another part of the
     image was to double-click again to zoom back out and re-click
     elsewhere. Once zoomed, simply moving the mouse now pans continuously
     to follow the cursor — the fix for "cannot hover around the image" —
     reaching every edge via the same clamped range panForOrigin/clampPan
     already use for pinch and swipe, not just the center.

     Only wired up on fine-pointer/hover-capable devices (mouse, trackpad):
     touch already has its own pinch-to-zoom + one-finger pan above, and a
     touchscreen doesn't fire continuous "mousemove" the way a mouse does
     (some browsers synthesize one right after a tap), so gating this on
     the same media features CSS uses for hover avoids the two zoom
     systems fighting over the same transform on a touch device. */
  var fineHover = window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  if (fineHover) {
    var pendingClientX = 0, pendingClientY = 0, panQueued = false;

    function followPointer() {
      panQueued = false;
      if (scale <= 1) return;
      var origin = clientToOrigin(pendingClientX, pendingClientY);
      var next = panForOrigin(origin[0], origin[1], scale);
      translateX = next[0];
      translateY = next[1];
      applyTransform(); // no withTransition() here on purpose — a fresh
      // CSS transition on every mousemove would fight the next one a few
      // ms later and read as laggy/rubber-bandy; an un-transitioned update
      // batched to one per animation frame (below) is what actually looks
      // smooth for continuous pointer-following.
    }

    container.addEventListener("mousemove", function (evt) {
      if (scale <= 1) return;
      pendingClientX = evt.clientX;
      pendingClientY = evt.clientY;
      if (panQueued) return;
      panQueued = true;
      requestAnimationFrame(followPointer);
    });

    img.addEventListener("dblclick", function (evt) {
      if (scale > 1) {
        toggleZoom();
      } else {
        var origin = clientToOrigin(evt.clientX, evt.clientY);
        toggleZoom(origin[0], origin[1]);
      }
    });

    /* Zooming back out on mouseleave — rather than requiring a second
       double-click — is what makes the whole thing feel dynamic rather
       than a fiddly toggle: hover in, look around, move away, done. */
    container.addEventListener("mouseleave", function () {
      if (scale > 1) resetZoom(true);
    });
  } else {
    /* Touch devices (or anything without hover): keep the simple toggle,
       still zooming in at the tapped point via the dblclick handler's own
       clientX/clientY rather than dead center. */
    img.addEventListener("dblclick", function (evt) {
      var origin = clientToOrigin(evt.clientX, evt.clientY);
      toggleZoom(origin[0], origin[1]);
    });
  }
})();
